import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Promo code state for registration checkout — validate explicitly via Apply (not on blur/debounce).
 * @param {() => object} getValidatePayload extra body fields for POST /auth/validate-promo-code
 */
export function useRegistrationPromoCode(getValidatePayload) {
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoCodeValid, setPromoCodeValid] = useState(false);
  const [promoCodeMessage, setPromoCodeMessage] = useState('');
  const [promoCodeData, setPromoCodeData] = useState(null);
  const [promoApplying, setPromoApplying] = useState(false);

  const onPromoInputChange = useCallback((value) => {
    setPromoCodeInput(value);
    setPromoCodeValid(false);
    setPromoCodeMessage('');
    setPromoCodeData(null);
  }, []);

  const applyPromoCode = useCallback(async () => {
    const code = (promoCodeInput || '').trim();
    if (!code) {
      setPromoCodeValid(false);
      setPromoCodeMessage('');
      setPromoCodeData(null);
      return;
    }

    setPromoApplying(true);
    try {
      const extra = typeof getValidatePayload === 'function' ? getValidatePayload() : {};
      const response = await api.post('/auth/validate-promo-code', {
        promoCode: code,
        ...extra,
      });

      if (response.success && response.valid) {
        setPromoCodeValid(true);
        setPromoCodeMessage(response.message);
        setPromoCodeData(response);
      } else {
        setPromoCodeValid(false);
        setPromoCodeMessage(response.message || 'Invalid promo code');
        setPromoCodeData(null);
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoCodeValid(false);
      setPromoCodeMessage(
        error.response?.data?.message || 'Could not validate promo code. Please try again.'
      );
      setPromoCodeData(null);
    } finally {
      setPromoApplying(false);
    }
  }, [promoCodeInput, getValidatePayload]);

  const appliedPromoCode =
    promoCodeValid && (promoCodeInput || '').trim() ? promoCodeInput.trim() : null;

  return {
    promoCodeInput,
    setPromoCodeInput,
    onPromoInputChange,
    promoCodeValid,
    promoCodeMessage,
    promoCodeData,
    promoApplying,
    applyPromoCode,
    appliedPromoCode,
  };
}
